/**
 * Materials on a phone — Board 16 C (`docs/visit-page.md` §19.2): the chips
 * (*By status · By day · Needs ordering · 1 · On the way · 2*, scrolling),
 * then the groups as cards and each line as a card row with its chip and
 * **Order** stacked on the right. Cards, never a table.
 *
 * A card opens in place, as a desktop row does (Board 2), to the line's story,
 * what it costs the visit and the line's ⋯ actions as a list — a phone has no
 * ⋯ to hang a menu from (DEVIATION REQUEST: the phone's line actions are not
 * drawn).
 */
import type { MaterialGroup, MaterialLine, MaterialsByStatus, VisitRead } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { COMMON, MATERIALS } from './copy.js'
import { GROUP_DOT, MaterialChip, NotedTag, count, qtyText } from './materials-parts.js'
import { StateDetail } from './materials-table.js'

/** Which slice of By status the phone shows — the two drawn filter chips, or everything. */
export type PhoneFilter = 'all' | 'needs_ordering' | 'on_the_way'

/**
 * The chip row (Board 16 C): 30 high, 11 either side, green when chosen; each
 * reaches 44 px for a thumb without drawing any bigger.
 */
export function PhoneChips({ view, filter, toOrder, onTheWay, onView, onFilter }: {
  view: 'status' | 'day'
  filter: PhoneFilter
  toOrder: number
  onTheWay: number
  onView: (view: 'status' | 'day') => void
  onFilter: (filter: PhoneFilter) => void
}) {
  const chip = (on: boolean, label: string, press: () => void) => (
    <button
      type="button"
      aria-pressed={on}
      onClick={press}
      className={cn(
        'relative inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-full border px-[11px]',
        'text-[12.5px] font-semibold after:absolute after:inset-x-0 after:-inset-y-[7px] after:content-[""]',
        on ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text',
      )}
    >
      {label}
    </button>
  )
  return (
    <div className="no-scrollbar -mx-4 flex gap-1.5 overflow-x-auto px-4 pb-2.5">
      {chip(view === 'status' && filter === 'all', MATERIALS.views.status, () => { onView('status'); onFilter('all') })}
      {chip(view === 'day', MATERIALS.views.day, () => onView('day'))}
      {chip(view === 'status' && filter === 'needs_ordering', MATERIALS.needsOrderingCount(count(toOrder)),
        () => { onView('status'); onFilter('needs_ordering') })}
      {chip(view === 'status' && filter === 'on_the_way', MATERIALS.onTheWayCount(count(onTheWay)),
        () => { onView('status'); onFilter('on_the_way') })}
    </div>
  )
}

export function PhoneStatus({ data, read, filter, openLine, onOpenLine, onOrder, canEdit, renderOpened }: {
  data: MaterialsByStatus
  read: VisitRead
  filter: PhoneFilter
  openLine: string | null
  onOpenLine: (lineId: string | null) => void
  onOrder: (line: MaterialLine) => void
  canEdit: boolean
  /** The line opened in place — its story, its cost and its actions. */
  renderOpened: (line: MaterialLine) => React.ReactNode
}) {
  const groups = filter === 'all' ? data.groups : data.groups.filter((group) => group.key === filter)
  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => (
        <PhoneGroup
          key={group.key}
          group={group}
          read={read}
          openLine={openLine}
          onOpenLine={onOpenLine}
          onOrder={onOrder}
          canEdit={canEdit}
          renderOpened={renderOpened}
        />
      ))}
    </div>
  )
}

function PhoneGroup({ group, read, openLine, onOpenLine, onOrder, canEdit, renderOpened }: {
  group: MaterialGroup
  read: VisitRead
  openLine: string | null
  onOpenLine: (lineId: string | null) => void
  onOrder: (line: MaterialLine) => void
  canEdit: boolean
  renderOpened: (line: MaterialLine) => React.ReactNode
}) {
  return (
    <section className="overflow-hidden rounded-record border border-line bg-surface">
      <div className="flex items-center gap-2 px-3.5 py-[11px]">
        <i aria-hidden="true" className={cn('inline-block h-2 w-2 rounded-full', GROUP_DOT[group.key])} />
        <b className="text-[13.5px]">{MATERIALS.groups[group.key]}</b>
        <span className="text-[13px] text-text-muted">{COMMON.count(count(group.count))}</span>
      </div>
      {group.lines.map((line) => {
        const open = openLine === line.id
        return (
          <div key={line.id} className="border-t border-line-faint">
            <div
              role="button"
              tabIndex={0}
              aria-expanded={open}
              data-line={line.id}
              onClick={() => onOpenLine(open ? null : line.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpenLine(open ? null : line.id) }
              }}
              className={cn(
                'flex min-h-[44px] gap-2.5 px-3.5 py-3 outline-none',
                open ? 'bg-green-band-bg' : line.state === 'to_order' && 'bg-amber-row',
              )}
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <b className="text-[14px]">{line.names.name}</b>
                <span className="text-[12px] text-text-muted">
                  {MATERIALS.qtySource(qtyText(line.qty, line.unit), MATERIALS.source[line.source])}
                </span>
                <span className="mt-0.5 text-[12px] text-text-muted"><StateDetail line={line} read={read} link={false} /></span>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className="flex items-center gap-1.5">
                  <MaterialChip state={line.state} />
                  {line.po?.entry === 'noted' && <NotedTag />}
                </span>
                {line.actions.order && canEdit && (
                  <button
                    type="button"
                    onClick={(event) => { event.stopPropagation(); onOrder(line) }}
                    className="relative inline-flex h-8 items-center justify-center whitespace-nowrap rounded-button border
                               border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white
                               after:absolute after:-inset-1.5 after:content-['']"
                  >
                    {MATERIALS.order}
                  </button>
                )}
              </div>
            </div>
            {open && renderOpened(line)}
          </div>
        )
      })}
    </section>
  )
}
